import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useSubscription } from '@/features/billing/contexts/SubscriptionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/shared/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface AppLoadingGateProps {
  children: ReactNode;
}

// Timeout for loading gate - if app doesn't load, show error but don't force clear
// ✅ INCREASED from 30s to 90s to handle slow networks and reduce false timeout triggers
const LOADING_TIMEOUT_MS = 90000; // 90 seconds

/**
 * AppLoadingGate
 * Prevents app from rendering until auth and subscription data are ready
 * Eliminates Flash of Incorrect Content (FOIC)
 * Redirects to /auth if session expires
 * Shows unified loading for profile setup flow
 * CRITICAL: Has timeout fallback to prevent infinite loading
 */
export function AppLoadingGate({ children }: AppLoadingGateProps) {
  const { isLoading: authLoading, userDataLoaded, isAuthenticated, sessionExpired } = useAuth();
  const { loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Check if we're in post-profile-setup flow (coming from profile setup)
  const isSettingUpDashboard = location.state?.fromProfileSetup === true;

  // Extra loading state for profile setup - keeps loading screen visible until modal shows
  const [setupComplete, setSetupComplete] = useState(!isSettingUpDashboard);

  // CRITICAL FIX: Timeout fallback to prevent infinite loading
  const [forceLoad, setForceLoad] = useState(false);
  const [loadingStartTime] = useState(Date.now());

  // Listen for custom event from ProfileSetupPage when modal is about to show
  useEffect(() => {
    const handleSetupComplete = () => {
      console.log('[AppLoadingGate] Setup complete, hiding loading screen');
      setSetupComplete(true);
    };

    window.addEventListener('profile-setup-complete', handleSetupComplete);
    return () => window.removeEventListener('profile-setup-complete', handleSetupComplete);
  }, []);

  // Reset setupComplete when navigating away and back
  useEffect(() => {
    if (!isSettingUpDashboard) {
      setSetupComplete(true);
    } else {
      setSetupComplete(false);
    }
  }, [isSettingUpDashboard]);

  // CRITICAL FIX: Timeout protection - clear session and redirect after timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      const loadingDuration = Date.now() - loadingStartTime;

      if (!isAppReady && !forceLoad) {
        console.error(
          '[AppLoadingGate] ⏱️ TIMEOUT after', loadingDuration + 'ms',
          '- Force loading app. State:',
          { authLoading, userDataLoaded, subscriptionLoading, setupComplete }
        );

        // ✅ FIX: Just force load instead of clearing session and redirecting
        // This prevents hard refresh and allows user to continue if data loads slowly
        setForceLoad(true);

        toast({
          title: "Loading is taking longer than usual",
          description: "Continuing anyway - some features may load shortly.",
          variant: "default",
        });

        // ❌ REMOVED: Don't clear session or redirect - this causes hard refresh
        // supabase.auth.signOut();
        // localStorage.clear();
        // navigate('/auth', { replace: true });
      }
    }, LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [authLoading, userDataLoaded, subscriptionLoading, setupComplete, forceLoad, loadingStartTime, toast, navigate]);

  // Redirect to auth page if user is not authenticated after loading completes
  useEffect(() => {
    if (!authLoading && userDataLoaded && !isAuthenticated && !sessionExpired) {
      console.log('[AppLoadingGate] No active session detected, redirecting to /auth');
      navigate('/auth', { replace: true });
    }
  }, [authLoading, userDataLoaded, isAuthenticated, sessionExpired, navigate]);

  // Wait for both auth and subscription to finish loading
  // For profile setup flow, also wait for setupComplete flag
  // CRITICAL FIX: Also allow forceLoad to bypass waiting
  const isAppReady = !authLoading && userDataLoaded && !subscriptionLoading && setupComplete;

  // DEBUG: Log loading states periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAppReady) {
        console.log('[AppLoadingGate] Still loading:', {
          authLoading,
          userDataLoaded,
          subscriptionLoading,
          setupComplete,
          isAppReady
        });
      }
    }, 5000); // Log every 5 seconds

    return () => clearInterval(interval);
  }, [authLoading, userDataLoaded, subscriptionLoading, setupComplete, isAppReady]);

  // Show loading screen while data is being fetched (unless timeout forces load)
  if (!isAppReady && !forceLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="relative w-16 h-16 mx-auto">
            {/* Spinning loader */}
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">Loading wlkthru</h2>
            <p className="text-sm text-muted-foreground">
              {isSettingUpDashboard ? 'Setting up your dashboard...' :
               authLoading ? 'Checking authentication...' :
               !userDataLoaded ? 'Loading user data...' :
               'Loading subscription...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render children if not authenticated (will redirect above)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
